import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "@phosphor-icons/react";

/**
 * Compact intro-audio player with a static decorative waveform.
 * Designed to live inside a therapist/mentor card.
 *
 * Props:
 *  - src: public URL to the audio file (mp3/wav)
 *  - testid: optional data-testid suffix for tests
 */
const BARS = 36;
// Pre-generated, deterministic bar heights for a natural-looking waveform
const HEIGHTS = Array.from({ length: BARS }, (_, i) => {
  const t = i / (BARS - 1);
  const env = Math.sin(t * Math.PI);                                  // 0 → 1 → 0
  const wobble = 0.55 + 0.45 * Math.sin(i * 1.7) * Math.cos(i * 0.9); // pseudo-random
  return Math.max(0.18, Math.min(1, env * 0.85 + wobble * 0.35));
});

export default function IntroAudioPlayer({ src, testid = "" }) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0 → 1
  const [duration, setDuration] = useState(0);

  // Stop animation loop when paused / unmounted
  const stopRaf = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  // Cleanup
  useEffect(() => () => { stopRaf(); }, []);

  const tick = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.duration && Number.isFinite(a.duration)) {
      setProgress(a.currentTime / a.duration);
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      stopRaf();
    } else {
      a.play().then(() => {
        setPlaying(true);
        rafRef.current = requestAnimationFrame(tick);
      }).catch(() => { /* autoplay blocked, etc. */ });
    }
  };

  const onLoadedMeta = (e) => setDuration(e.target.duration || 0);
  const onEnded = () => { setPlaying(false); setProgress(0); stopRaf(); };

  const seek = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const bar = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bar.left;
    const ratio = Math.max(0, Math.min(1, x / bar.width));
    a.currentTime = a.duration * ratio;
    setProgress(ratio);
  };

  const fmt = (s) => {
    if (!Number.isFinite(s) || s <= 0) return "0:00";
    const m = Math.floor(s / 60);
    const r = Math.floor(s % 60);
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  if (!src) return null;

  return (
    <div className="md-audio-player" data-testid={testid ? `intro-audio-${testid}` : "intro-audio"}>
      <button
        type="button"
        onClick={toggle}
        className="md-audio-play"
        aria-label={playing ? "Pause intro" : "Play intro"}
        data-testid={testid ? `intro-audio-toggle-${testid}` : "intro-audio-toggle"}
      >
        {playing ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
      </button>
      <button
        type="button"
        onClick={seek}
        className="md-audio-waveform"
        aria-label="Audio progress, click to seek"
      >
        {HEIGHTS.map((h, i) => {
          const past = (i + 0.5) / BARS <= progress;
          return (
            <span
              key={`bar-${i}-${h}`}
              className={`md-audio-bar ${past ? "past" : ""} ${playing && Math.abs((i / BARS) - progress) < 0.04 ? "now" : ""}`}
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          );
        })}
      </button>
      <span className="md-audio-time">{fmt(playing ? (progress * duration) : duration || 30)}</span>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={onLoadedMeta}
        onEnded={onEnded}
      />
    </div>
  );
}
